import { flattenConfigs } from './utils';
import { RenderedTab, TabConfig, TabParams } from './types';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useReducer,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import React from 'react';
import { useTabViewManager } from './tab-views';
import {
  InMemoryTabManagerStorageAdapter,
  TabManagerStorateAdapter,
  useTabManagerStorage,
} from './storage';
import { TabManagerAction, tabManagerReducer } from './reducer';
import { cn } from '../lib/utils';
import {
  NavigatorProvider,
  type Navigator,
} from '../actions/command-palette/navigator';

export type TabManager = {
  push: (tab: {
    route: string;
    title?: string;
    displayTitle?: string;
    icon?: string;
    params?: Record<string, string | undefined>;
  }) => void;
  navigate: (tabId: string) => void;
  close: (tabId: string) => void;
  activeTab: string | null;
  pushAndReplace: (tab: {
    route: string;
    title?: string;
    displayTitle?: string;
    icon?: string;
    params?: Record<string, string | undefined>;
  }) => void;
  renderedTabs: Record<string, RenderedTab>;
  reorderTabs: (tabIds: string[]) => void;
  updateTab: (tabId: string, params: Record<string, string | number>) => void;
  tabViewManager: ReturnType<typeof useTabViewManager>;
  closeAllTabs: () => void;
};

const TabManagerContext = createContext<TabManager | null>(null);

export const useTabManager = () => {
  const context = useContext(TabManagerContext);

  if (!context) {
    throw new Error('TabManager must be used within a TabManager');
  }

  return context;
};

export const useTabManagerOptional = (): TabManager | null => {
  return useContext(TabManagerContext);
};

type TabsProps = {
  tabConfigs: TabConfig[];
  children: React.ReactNode;
  wrapper?: (props: { children: React.ReactNode }) => ReactNode;
  fallback?: (props: { children?: React.ReactNode }) => ReactNode;
  storageAdapter?: TabManagerStorateAdapter;
  beforeNavigate: () => void;
};

export type TabManagerState = {
  activeTab: string | null;
  renderedTabs: Record<string, RenderedTab>;
  history: {
    activeTab: string | null;
    renderedTabs: string[];
  }[];
};

const convertStoredStateToTabManagerState = (params: {
  allTabConfigs: TabConfig[];
  storedState: {
    activeTab: string | null;
    tabs: string[];
  };
  wrapper: React.ComponentType<{ children: React.ReactNode }>;
  fallback: React.ComponentType<{ children?: React.ReactNode }>;
}): TabManagerState => {
  const reducer = tabManagerReducer(
    params.allTabConfigs,
    params.wrapper,
    params.fallback,
    new InMemoryTabManagerStorageAdapter() //noop
  );
  const renderedTabs: Record<string, RenderedTab> = {};

  let state: TabManagerState = {
    activeTab: null,
    renderedTabs,
    history: [],
  };

  params.storedState.tabs.forEach((tab) => {
    state = reducer(state, {
      type: 'PUSH_TAB',
      payload: {
        route: tab,
      },
    });
  });

  return {
    ...state,
    activeTab: params.storedState.activeTab,
    history: [],
  };
};

const useStorageSync = (
  dispatch: React.Dispatch<TabManagerAction>,
  adapter: TabManagerStorateAdapter
) => {
  //we only want to subscribe on first render, thus the empty deps
  useEffect(() => {
    const unsub = adapter.subscribe((state) => {
      dispatch({
        type: 'SYNC',
        payload: state,
      });
    });

    return () => {
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const TabManager: React.FC<TabsProps> = ({
  tabConfigs,
  children,
  wrapper: Wrapper = React.Fragment,
  fallback: Fallback = React.Fragment,
  beforeNavigate,
  storageAdapter = new InMemoryTabManagerStorageAdapter(),
}) => {
  const allTabConfigs = useMemo(() => flattenConfigs(tabConfigs), [tabConfigs]);
  const tabViewManager = useTabViewManager();
  const activeTabRef = useRef<string | null>(null);
  const [storageState, _, adapter] = useTabManagerStorage(storageAdapter);
  const [state, dispatch] = useReducer(
    tabManagerReducer(allTabConfigs, Wrapper, Fallback, adapter),
    convertStoredStateToTabManagerState({
      allTabConfigs,
      storedState: storageState,
      wrapper: Wrapper,
      fallback: Fallback,
    })
  );
  const { activeTab, renderedTabs } = state;

  useStorageSync(dispatch, adapter);

  const setActiveTab = useCallback((tabId: string | null) => {
    activeTabRef.current = tabId;
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tabId });
  }, []);

  const push = useCallback(
    (tab: {
      route: string;
      title?: string;
      displayTitle?: string;
      icon?: string;
      params?: Record<string, string | undefined>;
    }) => {
      beforeNavigate();
      dispatch({
        type: 'PUSH_TAB',
        payload: {
          ...tab,
        },
      });
    },
    [beforeNavigate]
  );

  const reorderTabs = useCallback(
    (newOrder: string[]) => {
      const newTabs = { ...renderedTabs };
      const newOrderMap = newOrder.reduce((acc, tabId, index) => {
        acc[tabId] = { ...newTabs[tabId]!, order: index };
        return acc;
      }, {} as Record<string, RenderedTab>);

      dispatch({
        type: 'REORDER_TABS',
        payload: { newOrder, tabs: newOrderMap },
      });
    },
    [renderedTabs]
  );

  const close = useCallback(
    (tabId: string) => {
      tabViewManager.unregisterTabView(tabId);
      dispatch({ type: 'CLOSE_TAB', payload: { tabId } });
    },
    [tabViewManager]
  );

  const navigate = useCallback(
    (tabId: string) => {
      beforeNavigate();
      setActiveTab(tabId);
    },
    [setActiveTab, beforeNavigate]
  );

  const pushAndReplace = useCallback(
    (tab: {
      route: string;
      title?: string;
      displayTitle?: string;
      icon?: string;
      params?: Record<string, string | undefined>;
    }) => {
      beforeNavigate();
      dispatch({
        type: 'PUSH_AND_REPLACE_TAB',
        payload: tab,
      });
    },
    [beforeNavigate]
  );

  const updateTab = useCallback(
    (tabId: string, params: Record<string, string | number>) => {
      dispatch({ type: 'UPDATE_TAB', payload: { tabId, params } });
    },
    []
  );

  const closeAllTabs = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_TABS' });
  }, []);

  // Update activeTabRef when activeTab changes
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Create navigator that uses tabManager.push
  const navigator: Navigator = useCallback(
    (route: string) => {
      push({ route });
    },
    [push]
  );

  return (
    <TabManagerContext.Provider
      value={{
        push,
        navigate,
        close,
        activeTab,
        renderedTabs,
        pushAndReplace,
        reorderTabs,
        tabViewManager,
        updateTab,
        closeAllTabs,
      }}
    >
      <NavigatorProvider navigator={navigator}>{children}</NavigatorProvider>
    </TabManagerContext.Provider>
  );
};

const ParamsContext = createContext<(TabParams & { tabId: string }) | null>(
  null
);

export const ParamsProvider = ({
  tabId,
  params,
  children,
}: {
  tabId: string;
  params: TabParams;
  children: ReactNode;
}) => {
  return (
    <ParamsContext.Provider value={{ ...params, tabId }}>
      {children}
    </ParamsContext.Provider>
  );
};

export const useTabParams = <T extends Omit<TabParams, 'tabId'>>(): T & {
  tabId: string;
} => {
  const params = useContext(ParamsContext);
  if (!params) {
    throw new Error('Params must be used within a ParamsProvider');
  }

  // Create a proxy that automatically decodes URL components for string values
  return new Proxy(params as unknown as T & { tabId: string }, {
    get(target, prop) {
      const value = target[prop as keyof typeof target];

      // Only decode string values that might be URL-encoded
      if (typeof value === 'string' && prop !== 'tabId') {
        try {
          return decodeURIComponent(value);
        } catch {
          // If decoding fails, return the original value
          return value;
        }
      }

      return value;
    },
  });
};

export const useActiveTab = () => {
  const { activeTab } = useTabManager();
  return activeTab;
};

/**
 * TabLink component that can render either as a button or as its child element
 *
 * @param to - The route to navigate to when clicked
 * @param children - The content to render
 * @param asChild - If true, renders as the child element instead of a button
 * @param className - CSS classes to apply (when not using asChild)
 * @param type - Navigation type: 'push' (default) or 'replace'
 * @param props - Additional button props (when not using asChild)
 */
type TabLinkProps = {
  to: string;
  children: ReactNode;
  asChild?: boolean;
  className?: string;
  type?: 'replace' | 'push';
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;

export const TabLink = ({
  to,
  children,
  asChild = false,
  type = 'push',
  className,
  ...props
}: TabLinkProps) => {
  const { push, pushAndReplace } = useTabManager();

  const handleClick = () => {
    if (type === 'replace') {
      pushAndReplace({ route: to });
    } else {
      push({ route: to });
    }
  };

  if (asChild) {
    // If asChild is true, clone the child and add onClick handler
    return React.cloneElement(children as React.ReactElement, {
      onClick: handleClick,
      ...props,
    });
  }

  // Default button rendering
  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn('cursor-pointer', className)}
      {...props}
    >
      {children}
    </button>
  );
};
